/**
 * HeroPhoneMockup — Animated 2-scene hero preview shown on the landing.
 *
 *   Scene A (≈3.8s): A real-estate "VENDE-SE" yard sign is scanned by a
 *                    phone. A pulsing beam connects the phone's camera to
 *                    the QR code on the sign — tells the story "client
 *                    scans the sign in real life".
 *   Transition (≈1s): The sign slides/fades out; the dashboard phone
 *                    slides up from the bottom.
 *   Scene B (≈8s):   The dashboard view — cards stagger in, the "+1 Lead"
 *                    toast cycles through different leads, the QR counter
 *                    increments. Tells the story "the agent receives the
 *                    lead instantly in their CRM".
 *   Loop back to Scene A.
 *
 * Floating badges on the phone are positioned OUTSIDE the device area so
 * they never cover the "Painel" header or the inner cards.
 *
 * Copy is Portuguese (Portugal) — "QR lido", "leituras", "Painel".
 * Pure Tailwind + CSS keyframes (no extra dependency).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Users,
  QrCode,
  Calendar,
  TrendingUp,
  Check,
  Bell,
  Sparkles,
} from "lucide-react";

const ROTATING_LEADS = [
  { name: "João Silva",    property: "T3 · Benfica" },
  { name: "Marta Costa",   property: "T2 · Cascais" },
  { name: "Pedro Antunes", property: "Moradia · Sintra" },
  { name: "Rita Pereira",  property: "T1 · Aveiro" },
];

// Total loop duration in ms — must match the CSS keyframe durations below.
const SCENE_A_MS    = 3800;   // sign scene
const TRANSITION_MS = 900;    // sign fades / phone slides up
const SCENE_B_MS    = 8000;   // dashboard scene visible
const LOOP_MS       = SCENE_A_MS + TRANSITION_MS + SCENE_B_MS;

const formatCounter = (n) => n.toLocaleString("pt-PT");

const HeroPhoneMockup = () => {
  // 'sign' | 'phone' — drives which scene is active.
  const [scene, setScene] = useState("sign");
  const [loopKey, setLoopKey] = useState(0); // forces remount of phone animations each loop

  const [leadIdx, setLeadIdx] = useState(0);
  const [scanCount, setScanCount] = useState(156);
  const [scanPulse, setScanPulse] = useState(false);
  const [showToast, setShowToast] = useState(true);

  // Master loop: A → transition → B → A
  useEffect(() => {
    let active = true;
    const tick = () => {
      if (!active) return;
      setScene("sign");
      setTimeout(() => {
        if (!active) return;
        setScene("phone");
        setLoopKey((k) => k + 1);
        // Reset counter at the start of each phone scene to add anticipation
        setScanCount(156 + Math.floor(Math.random() * 3));
      }, SCENE_A_MS);
      // Schedule next loop
      setTimeout(tick, LOOP_MS);
    };
    tick();
    return () => {
      active = false;
    };
  }, []);

  // Rotate the lead featured in the toast while the phone scene is active
  useEffect(() => {
    if (scene !== "phone") return undefined;
    const id = setInterval(() => {
      setShowToast(false);
      setTimeout(() => {
        setLeadIdx((i) => (i + 1) % ROTATING_LEADS.length);
        setShowToast(true);
      }, 320);
    }, 3000);
    return () => clearInterval(id);
  }, [scene]);

  // Counter increments visibly during the phone scene
  useEffect(() => {
    if (scene !== "phone") return undefined;
    const id = setInterval(() => {
      setScanCount((c) => c + (1 + Math.floor(Math.random() * 3)));
      setScanPulse(true);
      setTimeout(() => setScanPulse(false), 700);
    }, 2200);
    return () => clearInterval(id);
  }, [scene]);

  const currentLead = ROTATING_LEADS[leadIdx];

  const cards = useMemo(
    () => [
      {
        key: "lead",
        Icon: Users,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
        title: "Novo lead",
        subtitle: `${currentLead.name} — ${currentLead.property}`,
        accent: "border-emerald-500/20",
      },
      {
        key: "qr",
        Icon: QrCode,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
        title: "QR lido",
        subtitle: "Moradia Porto · +3 leituras",
        accent: "border-blue-500/20",
      },
      {
        key: "visit",
        Icon: Calendar,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
        title: "Visita agendada",
        subtitle: "Amanhã às 15h00",
        accent: "border-purple-500/20",
      },
      {
        key: "conv",
        Icon: TrendingUp,
        iconBg: "bg-amber-100",
        iconColor: "text-amber-600",
        title: "+45% Conversão",
        subtitle: "Este mês vs. anterior",
        accent: "border-amber-500/20",
      },
    ],
    [currentLead]
  );

  return (
    <div className="relative flex justify-center">
      <style>{`
        @keyframes imo-slide-in {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes imo-toast-in {
          from { opacity: 0; transform: translateX(-18px) scale(0.9); }
          to   { opacity: 1; transform: translateX(0)     scale(1); }
        }
        @keyframes imo-soft-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes imo-pulse-ring {
          0%   { box-shadow: 0 0 0 0   rgba(16, 185, 129, 0.45); }
          80%  { box-shadow: 0 0 0 14px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0   rgba(16, 185, 129, 0); }
        }
        @keyframes imo-counter-pulse {
          0%, 100% { color: #1a56db; transform: scale(1); }
          50%      { color: #10b981; transform: scale(1.08); }
        }
        @keyframes imo-beam {
          0%   { opacity: 0.0; transform: scaleX(0.5) translateX(-10%); }
          40%  { opacity: 0.85; transform: scaleX(1) translateX(0%); }
          100% { opacity: 0.0; transform: scaleX(1.05) translateX(10%); }
        }
        @keyframes imo-scan-line {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.9; }
          85%  { opacity: 0.9; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        @keyframes imo-phone-tilt {
          0%, 100% { transform: rotate(-9deg) translateY(0); }
          50%      { transform: rotate(-7deg) translateY(-3px); }
        }
        @keyframes imo-sign-sway {
          0%, 100% { transform: rotate(-1deg); }
          50%      { transform: rotate(1deg); }
        }
        @keyframes imo-fade-out {
          from { opacity: 1; transform: scale(1)   translateY(0); }
          to   { opacity: 0; transform: scale(0.9) translateY(-20px); }
        }
        @keyframes imo-fade-in-up {
          from { opacity: 0; transform: translateY(60px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes imo-shrink-away {
          0%   { opacity: 1; transform: scale(1) translateX(0); }
          100% { opacity: 0; transform: scale(0.4) translateX(-80px); }
        }
        .imo-card  { animation: imo-slide-in 0.55s cubic-bezier(.2,.8,.2,1) both; }
        .imo-toast { animation: imo-toast-in 0.45s cubic-bezier(.2,.8,.2,1) both, imo-soft-bounce 3.2s ease-in-out 0.6s infinite; }
        .imo-check { animation: imo-pulse-ring 2.4s ease-out infinite; }
        .imo-counter-anim { animation: imo-counter-pulse 0.7s ease-out; }
        .imo-beam  { animation: imo-beam 1.6s ease-in-out infinite; transform-origin: left center; }
        .imo-scan-line { animation: imo-scan-line 1.6s ease-in-out infinite; }
        .imo-phone-tilt { animation: imo-phone-tilt 2.4s ease-in-out infinite; transform-origin: bottom right; }
        .imo-sign-sway  { animation: imo-sign-sway  3.6s ease-in-out infinite; transform-origin: bottom center; }
        .imo-scene-out  { animation: imo-fade-out 0.8s ease-in forwards; }
        .imo-scene-in   { animation: imo-fade-in-up 0.85s cubic-bezier(.2,.8,.2,1) both; }
        .imo-sign-mini  { animation: imo-shrink-away 0.8s ease-in forwards; }
      `}</style>

      <div className="relative w-full max-w-[640px] h-[640px] flex items-center justify-center">

        {/* ============================================================== */}
        {/* SCENE A — Real-estate yard sign being scanned by a phone        */}
        {/* ============================================================== */}
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            scene === "sign" ? "imo-scene-in" : "imo-scene-out pointer-events-none"
          }`}
          aria-hidden={scene !== "sign"}
        >
          {/* Soft background blob */}
          <div className="absolute inset-x-12 inset-y-16 bg-gradient-to-br from-blue-100/40 via-indigo-100/30 to-purple-100/30 rounded-[3rem] blur-2xl" />

          {/* Step caption */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white border border-gray-100 rounded-full px-3 py-1.5 shadow-sm">
            <span className="w-5 h-5 rounded-full bg-[#1a56db] text-white text-[11px] font-bold flex items-center justify-center">1</span>
            <span className="text-xs font-semibold text-gray-700">O cliente lê a placa</span>
          </div>

          {/* Sign post + panel */}
          <div className="relative imo-sign-sway">
            {/* Vertical post */}
            <div className="absolute left-1/2 -translate-x-1/2 top-44 w-2 h-32 bg-gradient-to-b from-amber-900 to-amber-950 rounded-sm shadow" />

            {/* Sign panel */}
            <div className="relative w-[230px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-rose-500 to-red-600 px-3 py-2 flex items-center justify-between">
                <span className="text-white font-extrabold text-sm tracking-wider">VENDE-SE</span>
                <span className="text-[10px] font-semibold text-white/90 bg-white/15 px-1.5 py-0.5 rounded">IMOCODE</span>
              </div>

              {/* Body */}
              <div className="p-3">
                <p className="text-gray-900 font-bold text-base leading-tight">T3 · Lisboa</p>
                <p className="text-gray-500 text-[11px] mb-2">Av. da República · 96m²</p>
                <p className="text-[#1a56db] font-extrabold text-2xl leading-none">€295.000</p>

                {/* QR + scan animation */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="relative w-20 h-20 bg-white border border-gray-200 rounded-md p-1 overflow-hidden">
                    {/* Stylised QR using a grid pattern */}
                    <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden>
                      <rect x="0" y="0" width="100" height="100" fill="#fff" />
                      {/* finder squares */}
                      <rect x="4" y="4"   width="22" height="22" fill="#0f172a" />
                      <rect x="9" y="9"   width="12" height="12" fill="#fff" />
                      <rect x="13" y="13" width="4"  height="4"  fill="#0f172a" />
                      <rect x="74" y="4"  width="22" height="22" fill="#0f172a" />
                      <rect x="79" y="9"  width="12" height="12" fill="#fff" />
                      <rect x="83" y="13" width="4"  height="4"  fill="#0f172a" />
                      <rect x="4" y="74"  width="22" height="22" fill="#0f172a" />
                      <rect x="9" y="79"  width="12" height="12" fill="#fff" />
                      <rect x="13" y="83" width="4"  height="4"  fill="#0f172a" />
                      {/* random data modules */}
                      {[
                        [32,8],[40,8],[48,8],[56,12],[64,8],
                        [32,16],[44,20],[52,16],[60,20],
                        [8,32],[16,32],[24,32],[32,36],[40,32],[48,36],[56,32],[64,36],[72,32],[80,32],[88,36],
                        [8,40],[20,40],[28,44],[36,40],[44,44],[52,40],[60,44],[68,40],[76,44],[84,40],[92,40],
                        [12,52],[20,56],[28,52],[40,56],[52,52],[64,56],[72,52],[80,56],[88,52],
                        [8,64],[16,68],[24,64],[32,68],[44,64],[56,68],[60,64],[72,68],[80,64],[88,68],
                        [36,76],[44,80],[52,76],[60,80],[68,76],[84,76],
                        [40,84],[48,88],[56,84],[64,88],[72,84],[88,88],
                      ].map(([x, y], i) => (
                        <rect key={i} x={x} y={y} width="4" height="4" fill="#0f172a" />
                      ))}
                    </svg>
                    {/* Scan line */}
                    {scene === "sign" && (
                      <div className="absolute inset-x-1 top-1 h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent imo-scan-line" />
                    )}
                  </div>
                  <div className="flex-1 leading-snug">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Leia o código</p>
                    <p className="text-[11px] text-gray-700">e veja o imóvel no telemóvel</p>
                  </div>
                </div>

                {/* Realtor tag */}
                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-gray-700">Imobiliária Lar Real</span>
                  <span className="text-[10px] text-gray-400">+351 21 000 0000</span>
                </div>
              </div>
            </div>

            {/* Hand + phone scanning the QR — shows a tiny preview of the
                property card on its screen so the storytelling is clearer:
                "the buyer is already seeing the listing". On mobile we shift
                the phone slightly inwards so it never escapes the hero box. */}
            <div className="absolute -right-16 sm:-right-24 top-32 imo-phone-tilt">
              {/* Animated scan beam between phone camera & QR */}
              {scene === "sign" && (
                <div className="absolute -left-10 top-12 w-20 h-[3px] bg-gradient-to-l from-emerald-400 to-transparent imo-beam rounded-full" />
              )}
              <div className="relative w-24 h-40 bg-gray-900 rounded-2xl p-1 shadow-2xl">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1.5 bg-gray-900 rounded-b-md z-10" />
                <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col">
                  {/* Mini preview of the property the QR points to */}
                  <div className="h-12 bg-gradient-to-br from-blue-500 to-indigo-600 relative">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.3),transparent_50%)]" />
                    <div className="absolute top-1.5 left-1.5 text-[8px] font-bold text-white bg-rose-500 px-1 rounded">À VENDA</div>
                  </div>
                  <div className="flex-1 p-1.5 space-y-1">
                    <p className="text-[8px] font-bold text-gray-900 leading-tight">T3 · Lisboa</p>
                    <p className="text-[7px] text-gray-500 leading-tight">96m²</p>
                    <p className="text-[9px] font-extrabold text-[#1a56db] leading-none">€295.000</p>
                    <div className="flex gap-0.5 pt-0.5">
                      <div className="h-1 flex-1 rounded-sm bg-emerald-400" />
                      <div className="h-1 flex-1 rounded-sm bg-emerald-400" />
                      <div className="h-1 flex-1 rounded-sm bg-gray-200" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Helper hint under the scene */}
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 font-medium">
            <span className="text-[#1a56db] font-bold">QR único</span> por imóvel
          </p>
        </div>

        {/* ============================================================== */}
        {/* SCENE B — The agent's dashboard receives the lead instantly     */}
        {/* ============================================================== */}
        <div
          key={loopKey}
          className={`absolute inset-0 flex items-center justify-center ${
            scene === "phone" ? "imo-scene-in" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={scene !== "phone"}
        >
          {/* Step caption */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white border border-gray-100 rounded-full px-3 py-1.5 shadow-sm">
            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center">2</span>
            <span className="text-xs font-semibold text-gray-700">O agente recebe o lead</span>
          </div>

          <div className="relative">
            {/* Phone Frame */}
            <div className="relative w-72 h-[580px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-10" />

              <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                {/* App header */}
                <div className="p-4 pt-7 bg-gradient-to-br from-[#1a56db] via-[#1f5fe6] to-[#3b82f6] h-40 relative">
                  <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />

                  <div className="relative flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-white/25 ring-2 ring-white/40 flex items-center justify-center text-white font-bold text-sm">
                        SR
                      </div>
                      <div className="leading-tight">
                        <p className="text-[10px] text-blue-100">Olá,</p>
                        <p className="text-white font-semibold text-sm">Sofia R.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative w-8 h-8 bg-white/15 rounded-full flex items-center justify-center">
                        <Bell className="w-4 h-4 text-white" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          3
                        </span>
                      </div>
                      <div className="w-8 h-8 bg-white/15 rounded-full flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>

                  <h3 className="relative text-white font-bold text-xl tracking-tight">Painel</h3>
                  <p className="relative text-blue-100 text-sm">5 novos leads hoje</p>
                </div>

                {/* Cards */}
                <div className="p-4 space-y-3 -mt-8 relative">
                  {cards.map((card, i) => (
                    <div
                      key={card.key}
                      className={`imo-card bg-white rounded-2xl p-3.5 shadow-lg border ${card.accent}`}
                      style={{ animationDelay: `${0.15 + i * 0.12}s` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${card.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <card.Icon className={`w-5 h-5 ${card.iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 text-sm truncate">{card.title}</p>
                          <p className="text-xs text-gray-500 truncate">{card.subtitle}</p>
                        </div>
                        {card.key === "lead" && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            AGORA
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating toast — positioned to the LEFT of the phone so it
                NEVER overlaps the header. Hidden on small viewports where
                space is too tight; the toast would otherwise clip outside
                the hero container. */}
            <div
              className={`hidden md:block absolute -left-48 top-20 bg-white rounded-2xl p-3 shadow-xl border border-gray-100 w-44 ${
                showToast ? "imo-toast" : "opacity-0"
              }`}
              aria-live="polite"
            >
              <div className="flex items-center gap-3">
                <div className="imo-check w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm">+1 Lead</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {currentLead.name}
                  </p>
                  <p className="text-[10px] text-gray-400">agora mesmo</p>
                </div>
              </div>
            </div>

            {/* Floating counter — to the RIGHT, fully OUTSIDE the phone so
                it does not cover any of the inner cards. Hidden on small
                viewports for the same clipping reason as the toast above. */}
            <div className="hidden md:block absolute -right-44 bottom-24 bg-white rounded-2xl p-3 shadow-xl border border-gray-100 w-44">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-[#1a56db]" />
                </div>
                <div className="leading-tight">
                  <p className="text-[11px] text-gray-500 font-medium">QR ativo</p>
                  <p
                    className={`font-bold text-base ${
                      scanPulse ? "imo-counter-anim" : "text-[#1a56db]"
                    }`}
                  >
                    {formatCounter(scanCount)}{" "}
                    <span className="text-[11px] font-medium text-gray-500">leituras</span>
                  </p>
                </div>
              </div>
              <div className="mt-1.5 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#1a56db] to-emerald-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((scanCount - 153) / 50) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroPhoneMockup;
